import { useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { Card, Button } from "@/design-system/components/ui";
import { Input } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { cn } from "@/design-system/lib/utils";
import {
  useCommitComposition,
  useContributeComposition,
  usePoolComposition,
  usePoolEligibleSegments,
  useProposeSegments,
  useValidateComposition,
} from "@/hooks/capitalMarkets/useSpvPools";
import type {
  CompositionValidationResult,
  PoolCompositionDto,
  SpvPoolDto,
} from "@/types/spvPool";
import { formatCurrency } from "../../shared/format";

export function CompositionTab({ pool }: { pool: SpvPoolDto }) {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const canBuild = hasPermission("SpvPool.Build");
  const canContribute = hasPermission("SpvPool.Contribute");

  const eligibleQuery = usePoolEligibleSegments(pool.id);
  const compositionQuery = usePoolComposition(pool.id);

  const propose = useProposeSegments(pool.id);
  const validate = useValidateComposition(pool.id);
  const commit = useCommitComposition(pool.id);
  const contribute = useContributeComposition(pool.id);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [ratingFilter, setRatingFilter] = useState<string | null>(null);
  const [validationResult, setValidationResult] =
    useState<CompositionValidationResult | null>(null);
  const [contributeOpen, setContributeOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");

  const eligibleLoans = eligibleQuery.data ?? [];
  const composition = useMemo(
    () => compositionQuery.data ?? [],
    [compositionQuery.data],
  );

  const ratings = useMemo(
    () => [...new Set(eligibleLoans.map((l) => l.creditRating))].filter(Boolean),
    [eligibleLoans],
  );

  const filteredLoans = ratingFilter
    ? eligibleLoans.filter((l) => l.creditRating === ratingFilter)
    : eligibleLoans;

  const proposed = composition.filter((c) => c.status === "Proposed");
  const committed = composition.filter((c) => c.status === "Committed");
  const contributed = composition.filter((c) => c.status === "Contributed");

  const selectedIds = [...selected];

  const toggleSegment = (segmentId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(segmentId)) next.delete(segmentId);
      else next.add(segmentId);
      return next;
    });
  };

  const toggleExpand = (loanId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(loanId)) next.delete(loanId);
      else next.add(loanId);
      return next;
    });
  };

  const handlePropose = () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one segment to propose.");
      return;
    }
    propose
      .mutateAsync(selectedIds)
      .then(() => {
        toast.success(`Proposed ${selectedIds.length} segment(s).`);
        setSelected(new Set());
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const handleValidate = () => {
    validate
      .mutateAsync()
      .then((result) => {
        setValidationResult(result);
        if (result.passed) toast.success("Composition passed all checks.");
        else toast.error("Composition has unresolved breaches.");
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const handleCommit = () => {
    const ids =
      selectedIds.length > 0 ? selectedIds : proposed.map((p) => p.segmentId);
    if (ids.length === 0) {
      toast.error("Nothing to commit.");
      return;
    }
    commit
      .mutateAsync(ids)
      .then((result) => {
        setValidationResult(result);
        if (result.passed) toast.success("Composition committed.");
        else toast.error("Commit blocked by concentration breaches.");
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  const handleContribute = () => {
    const ids =
      selectedIds.length > 0 ? selectedIds : committed.map((c) => c.segmentId);
    if (ids.length === 0) {
      toast.error("Nothing to contribute.");
      return;
    }
    contribute
      .mutateAsync({ segmentIds: ids, confirmPoolName: confirmName })
      .then((result) => {
        toast.success(`Contributed ${result.contributed} segment(s).`);
        setContributeOpen(false);
        setConfirmName("");
        setSelected(new Set());
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <div className="flex flex-col gap-space-4">
      {validationResult && !validationResult.passed && (
        <div className="border-l-[3px] border-status-error bg-status-error/10 p-space-3 rounded-md">
          <div className="flex items-center gap-space-2 font-display font-semibold text-caption uppercase tracking-wide text-status-error mb-space-2">
            <AlertTriangle size={14} strokeWidth={1.5} aria-hidden />
            Concentration / eligibility breaches
          </div>
          <ul className="m-0 pl-space-4 list-disc text-small">
            {validationResult.rules.map((r) => (
              <li
                key={r.rule}
                className={cn(
                  r.passed ? "text-status-success" : "text-status-error",
                )}
              >
                <span className="font-semibold">{r.rule}</span>: {r.detail}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-space-4">
        <Card>
          <div className="flex items-center justify-between mb-space-3">
            <h3 className="font-display font-bold text-h3 m-0">
              Eligible Loans
            </h3>
          </div>
          <div className="flex flex-wrap gap-space-2 mb-space-3">
            <Chip
              label="All ratings"
              active={ratingFilter === null}
              onClick={() => setRatingFilter(null)}
            />
            {ratings.map((r) => (
              <Chip
                key={r}
                label={r}
                active={ratingFilter === r}
                onClick={() => setRatingFilter(r)}
              />
            ))}
          </div>
          {eligibleQuery.isLoading ? (
            <p className="text-small text-gray-mid italic m-0">
              Loading eligible loans…
            </p>
          ) : filteredLoans.length === 0 ? (
            <p className="text-small text-gray-mid italic m-0">
              No eligible loans found.
            </p>
          ) : (
            <ul className="flex flex-col gap-space-1 m-0 p-0 list-none">
              {filteredLoans.map((loan) => {
                const open = expanded.has(loan.loanId);
                return (
                  <li
                    key={loan.loanId}
                    className="border border-border-light rounded-md"
                  >
                    <button
                      type="button"
                      onClick={() => toggleExpand(loan.loanId)}
                      className="w-full flex items-center gap-space-2 px-space-3 py-space-2 text-left"
                    >
                      {open ? (
                        <ChevronDown size={14} aria-hidden />
                      ) : (
                        <ChevronRight size={14} aria-hidden />
                      )}
                      <span className="font-semibold text-small">
                        {loan.loanNumber}
                      </span>
                      <span className="text-caption text-gray-mid">
                        {loan.borrowerName}
                      </span>
                      <span className="ml-auto text-caption text-gray-mid">
                        {loan.creditRating} · {loan.status}
                      </span>
                    </button>
                    {open && (
                      <div className="border-t border-border-light px-space-3 py-space-2 flex flex-col gap-space-1">
                        {loan.segments.map((seg) => (
                          <label
                            key={seg.segmentId}
                            className={cn(
                              "flex items-center gap-space-2 text-small",
                              seg.alreadyPooled && "opacity-50",
                            )}
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-gold"
                              disabled={seg.alreadyPooled || !canBuild}
                              checked={selected.has(seg.segmentId)}
                              onChange={() => toggleSegment(seg.segmentId)}
                            />
                            <span>Segment #{seg.segmentNumber}</span>
                            <span className="ml-auto text-caption text-gray-mid">
                              {formatCurrency(seg.currentPrincipalBalance)}
                              {seg.alreadyPooled && " · pooled"}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {canBuild && (
            <div className="mt-space-3">
              <Button
                variant="secondary"
                size="sm"
                loading={propose.isPending}
                onClick={handlePropose}
              >
                Propose selected ({selected.size})
              </Button>
            </div>
          )}
        </Card>

        <Card>
          <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
            Pool Composition
          </h3>
          <CompositionSection title="Proposed" rows={proposed} />
          <CompositionSection title="Committed" rows={committed} />
          <CompositionSection title="Contributed" rows={contributed} />
        </Card>
      </div>

      <Card>
        <div className="flex flex-wrap items-center gap-space-3">
          <Button
            variant="secondary"
            size="sm"
            disabled={!canBuild}
            loading={validate.isPending}
            onClick={handleValidate}
          >
            Validate Composition
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!canBuild}
            loading={commit.isPending}
            onClick={handleCommit}
          >
            Commit Composition
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!canContribute}
            onClick={() => setContributeOpen(true)}
          >
            Contribute Now
          </Button>
          {validationResult?.passed && (
            <span className="text-caption text-status-success font-display uppercase tracking-wide">
              All checks passed
            </span>
          )}
        </div>
      </Card>

      <Modal
        open={contributeOpen}
        onClose={() => setContributeOpen(false)}
        dismissOnBackdropClick={false}
        title="Contribute segments to pool"
        size="sm"
        footer={
          <div className="flex items-center justify-end gap-space-2">
            <Button
              variant="text"
              size="sm"
              onClick={() => setContributeOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              loading={contribute.isPending}
              disabled={confirmName !== pool.poolName}
              onClick={handleContribute}
            >
              Contribute permanently
            </Button>
          </div>
        }
      >
        <p className="text-small text-status-error font-semibold">
          This action is permanent. The selected segments will be permanently
          locked to this pool and cannot be moved or removed.
        </p>
        <p className="text-small text-gray-mid">
          Type the pool name{" "}
          <span className="font-mono text-onyx">{pool.poolName}</span> to
          confirm.
        </p>
        <Input
          value={confirmName}
          onChange={(e) => setConfirmName(e.target.value)}
          placeholder={pool.poolName}
          aria-label="Confirm pool name"
        />
      </Modal>
    </div>
  );
}

function CompositionSection({
  title,
  rows,
}: {
  title: string;
  rows: PoolCompositionDto[];
}) {
  return (
    <div className="mb-space-3">
      <div className="text-label font-display uppercase tracking-label text-gold-shadow mb-space-1">
        {title} ({rows.length})
      </div>
      {rows.length === 0 ? (
        <p className="text-caption text-gray-mid italic m-0">None</p>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col gap-[2px]">
          {rows.map((r) => (
            <li
              key={r.id}
              className="flex items-center justify-between text-small border-b border-border-light py-space-1"
            >
              <span>Segment #{r.segmentNumber}</span>
              <span className="text-caption text-gray-mid">
                {formatCurrency(r.currentPrincipalBalance)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-space-2 py-[2px] rounded-full text-caption font-display uppercase tracking-wide border",
        active
          ? "bg-gold text-onyx border-gold font-semibold"
          : "bg-white-pure text-gray-mid border-border-light hover:text-onyx",
      )}
    >
      {label}
    </button>
  );
}
